import * as React from "react";
import { Crop, AspectRatio } from "core/models";
import $ from "./styles.module.scss";

type Handle = "top-left" | "bottom-left" | "bottom-right" | "top-right";

interface Props {
    onChange: (trim: Crop) => void;
    aspectRatio: AspectRatio;
}

interface Dimensions {
    x: number;
    y: number;
    width: number;
    height: number;
}

const TrimmingArea: React.FC<Props> = ({ onChange, aspectRatio }) => {
    const containerRef = React.useRef<HTMLDivElement>(null);
    const [initialX, setInitialX] = React.useState<number>(0);
    const [initialY, setInitialY] = React.useState<number>(0);
    const [initialTrim, setInitialTrim] = React.useState<Crop>();
    const [initialMouseX, setInitialMouseX] = React.useState<number>(0);
    const [initialMouseY, setInitialMouseY] = React.useState<number>(0);
    const [isDragging, setIsDragging] = React.useState<boolean>(false);
    const [draggingHandle, setDraggingHandle] = React.useState<Handle>();
    const [crop, setCrop] = React.useState<Crop>(new Crop());
    const [dimensions, setDimensions] = React.useState<Dimensions>({
        x: 0,
        y: 0,
        width: 0,
        height: 0,
    });

    const setNewAspectRatio = React.useCallback(
        (newAspectRatio: AspectRatio) => {
            const containerEl = containerRef.current;
            if (!containerEl) {
                return;
            }
            const newCrop = { ...crop };
            newCrop.size.height = containerEl.offsetHeight;
            switch (newAspectRatio) {
                case "1:1":
                    newCrop.size.width = containerEl.offsetHeight;
                    break;
                case "4:5":
                    newCrop.size.width = Math.ceil(
                        (containerEl.offsetHeight / 5) * 4,
                    );
                    break;
                case "9:16":
                    newCrop.size.width = Math.ceil(
                        (containerEl.offsetHeight / 16) * 9,
                    );
                    break;
                case "1:2":
                    newCrop.size.width = Math.ceil(
                        containerEl.offsetHeight / 2,
                    );
                    break;
                case "2:3":
                    newCrop.size.width = Math.ceil(
                        (containerEl.offsetHeight / 3) * 2,
                    );
                    break;
                case "2:1":
                    newCrop.size.height = Math.ceil(
                        containerEl.offsetWidth / 2,
                    );
                    newCrop.size.width = containerEl.offsetWidth;
                    break;
                case "16:9":
                    newCrop.size.height = containerEl.offsetHeight;
                    newCrop.size.width = containerEl.offsetWidth;
                    break;
                default:
                    throw new Error("this should not happen");
            }
            newCrop.outputSize.width = newCrop.size.width;
            newCrop.outputSize.height = newCrop.size.height;
            newCrop.point = {
                x: Math.floor(
                    containerEl.offsetWidth / 2 - newCrop.size.width / 2,
                ),
                y: 0,
            };
            setCrop(newCrop);
            onChange(newCrop);
        },
        [containerRef, onChange, crop],
    );

    React.useEffect(() => {
        const container = containerRef.current;
        if (!crop) {
            setCrop(new Crop());
        }
        if (container) {
            const newCrop = { ...crop };
            newCrop.outputSize.height = container.offsetHeight;
            setNewAspectRatio(aspectRatio);
            newCrop.point.x =
                container.offsetWidth / 2 - Math.floor(newCrop.size.width / 2);
            setCrop(newCrop);
        }
    }, []);

    React.useEffect(() => {
        setNewAspectRatio(aspectRatio);
    }, [aspectRatio]);

    const handleResizeMouseDown = (ev: React.MouseEvent, handle: Handle) => {
        ev.preventDefault();
        setDraggingHandle(handle);
        setInitialMouseX(ev.clientX);
        setInitialMouseY(ev.clientY);
        setInitialTrim(new Crop(crop));
    };

    const handleResizeMouseMove = (ev: MouseEvent, handle: Handle) => {
        const containerEl = containerRef.current;
        if (!containerEl) {
            return;
        }
        if (!initialTrim) {
            return;
        }

        const { clientX } = ev;
        const containerHeight = containerEl.offsetHeight;
        const initialSize = initialTrim.size;
        const initialPoint = initialTrim.point;
        const dX = clientX - initialMouseX;
        const dY = dX * (initialSize.height / initialSize.width);
        const newCrop = { ...crop };

        if (handle === "top-left" || handle === "bottom-left") {
            newCrop.size.height = initialSize.height - dY;
        } else {
            newCrop.size.height = initialSize.height + dY;
        }

        switch (handle) {
            case "top-left":
                newCrop.point.x = initialPoint.x + dX;
                newCrop.point.y = initialPoint.y + dY;
                break;
            case "bottom-left":
                newCrop.point.x = initialPoint.x + dX;
                break;
            case "top-right":
                newCrop.point.y = initialPoint.y - dY;
                break;
            default:
            // throw new Error('this should not happen');
        }

        const { size, point } = newCrop;
        if (point.x < 0) {
            point.x = 0;
        }
        if (point.y < 0) {
            point.y = 0;
        }
        newCrop.point = point;
        if (size.height + point.y > containerHeight) {
            newCrop.size.height = containerHeight - point.y;
        }
        newCrop.size.width =
            newCrop.size.height * (initialSize.width / initialSize.height);

        const { x, y } = newCrop.point;
        const { width, height } = newCrop.size;

        newCrop.outputSize.width = newCrop.size.width;
        newCrop.outputSize.height = newCrop.size.height;

        setCrop(newCrop);
        onChange(newCrop);
        setDimensions({
            x,
            y,
            width,
            height,
        });
    };

    const handleMouseMove = (ev: MouseEvent) => {
        if (draggingHandle) {
            handleResizeMouseMove(ev, draggingHandle);
            return;
        }
        const containerEl = containerRef.current;
        if (!containerEl) {
            return;
        }

        const { clientX, clientY } = ev;
        const containerWidth = containerEl.offsetWidth;
        const containerHeight = containerEl.offsetHeight;
        const { width, height } = crop.size;

        const nextX = Math.max(0, initialX + (clientX - initialMouseX));
        const nextY = Math.max(0, initialY + (clientY - initialMouseY));

        // Prevent scrolling outside of the player bounds
        const maxX = containerWidth - width;
        const maxY = containerHeight - height;
        if (isDragging) {
            crop.point.x = maxX < nextX ? maxX : nextX;
            crop.point.y = maxY < nextY ? maxY : nextY;
            setDimensions({
                ...dimensions,
                x: crop.point.x,
                y: crop.point.y,
            });
        }
    };

    const handleMouseUp = () => {
        setDraggingHandle(undefined);
        setIsDragging(false);
        document.body.removeEventListener("mouseup", handleMouseUp);
        document.body.removeEventListener("mousemove", handleMouseMove);
        const containerEl = containerRef.current;
        if (!containerEl) {
            return;
        }
        onChange(crop);
    };
    React.useEffect(() => {
        if (!isDragging) {
            return;
        }
        document.body.addEventListener("mouseup", handleMouseUp);
        document.body.addEventListener("mousemove", handleMouseMove);
    }, [isDragging]);

    const handleMouseDown: React.MouseEventHandler<HTMLDivElement> = (ev) => {
        if (isDragging) {
            ev.preventDefault();
        }
        setIsDragging(true);
        setInitialMouseX(ev.clientX);
        setInitialMouseY(ev.clientY);
        setInitialX(crop.point.x);
        setInitialY(crop.point.y);
    };

    const blockStyles = {
        top: `${crop.point.y}px`,
        left: `${crop.point.x}px`,
        width: `${crop.size.width}px`,
        height: `${crop.size.height}px`,
    };
    return (
        <div ref={containerRef} className={$.trim__container}>
            <div
                onMouseDown={handleMouseDown}
                style={blockStyles}
                role="presentation"
                className={$.trim__block}
            >
                <div
                    onMouseDown={(ev) => handleResizeMouseDown(ev, "top-left")}
                    className={`${$.trim__handle} ${$.handle__top__left}`}
                    role="presentation"
                />
                <div
                    onMouseDown={(ev) =>
                        handleResizeMouseDown(ev, "bottom-left")
                    }
                    className={`${$.trim__handle} ${$.handle__bottom__left}`}
                    role="presentation"
                />
                <div
                    onMouseDown={(ev) =>
                        handleResizeMouseDown(ev, "bottom-right")
                    }
                    className={`${$.trim__handle} ${$.handle__bottom__right}`}
                    role="presentation"
                />
                <div
                    onMouseDown={(ev) => handleResizeMouseDown(ev, "top-right")}
                    className={`${$.trim__handle} ${$.handle__top__right}`}
                    role="presentation"
                />
                <div className={$.blockGridRow}>
                    <div className={$.block__grid__row__item} />
                    <div className={$.block__grid__row__item} />
                    <div className={$.block__grid__row__item} />
                </div>
                <div className={$.blockGridRow}>
                    <div className={$.block__grid__row__item} />
                    <div className={$.block__grid__row__item} />
                    <div className={$.block__grid__row__item} />
                </div>
                <div className={$.blockGridRow}>
                    <div className={$.block__grid__row__item} />
                    <div className={$.block__grid__row__item} />
                    <div className={$.block__grid__row__item} />
                </div>
            </div>
        </div>
    );
};
export default TrimmingArea;
